// Bundle-hotspots section runtime: hoists per-block card templates, totals prices, wires hover + bulk add.
// Templates are needed because section.blocks settings are opaque on Horizon's @theme-wrapped blocks.

import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

class BundleHotspots extends HTMLElement {
  /** @type {HTMLElement[]} */
  cards = [];
  /** @type {HTMLElement[]} */
  hotspots = [];
  /** @type {HTMLButtonElement | null} */
  addAllButton = null;
  /** @type {(() => void) | undefined} */
  cleanupHoverLinking;
  /** @type {(() => void) | undefined} */
  cleanupBulkAdd;
  /** @type {((event: Event) => void) | undefined} */
  handleAddAll;

  connectedCallback() {
    this.relocateBgBlocks();
    this.hoistCards();
    this.computeTotal();
    this.wireHoverLinking();
    this.wireBulkAdd();
  }

  disconnectedCallback() {
    this.cleanupHoverLinking?.();
    this.cleanupBulkAdd?.();
  }

  relocateBgBlocks() {
    // bg blocks render inside .hotspots-container by default; they need to escape it to span the section.
    const bgLayer = this.querySelector('.section-bundle-hotspots__bg-layer');
    const hotspotsContainer = this.querySelector('.hotspots-container');
    if (!bgLayer || !hotspotsContainer) return;
    hotspotsContainer
      .querySelectorAll('.background-illustration, .background-pattern')
      .forEach((el) => bgLayer.appendChild(el));
  }

  hoistCards() {
    const cardsList = this.querySelector('.section-bundle-hotspots__cards');
    if (!cardsList) return;

    const sources = /** @type {NodeListOf<HTMLTemplateElement>} */ (
      this.querySelectorAll('template.bundle-card-source')
    );
    sources.forEach((tpl) => {
      const card = /** @type {Element | null} */ (tpl.content.firstElementChild?.cloneNode(true) ?? null);
      if (!card) return;
      const li = document.createElement('li');
      li.className = 'section-bundle-hotspots__card-item';
      li.appendChild(card);
      cardsList.appendChild(li);
    });
  }

  computeTotal() {
    const sources = /** @type {HTMLTemplateElement[]} */ (
      Array.from(this.querySelectorAll('template.bundle-card-source'))
    );
    if (sources.length === 0) return;

    let priceTotal = 0;
    let compareTotal = 0;
    let allAvailable = true;
    for (const tpl of sources) {
      const price = parseInt(tpl.dataset.price ?? '0', 10);
      const compare = parseInt(tpl.dataset.compareAtPrice ?? tpl.dataset.price ?? '0', 10);
      priceTotal += Number.isFinite(price) ? price : 0;
      compareTotal += Number.isFinite(compare) ? compare : 0;
      if (tpl.dataset.available !== 'true') allAvailable = false;
    }

    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';

    const nowEl = this.querySelector('.section-bundle-hotspots__total-now');
    const wasEl = /** @type {HTMLElement | null} */ (
      this.querySelector('.section-bundle-hotspots__total-was')
    );
    if (nowEl) nowEl.textContent = formatMoney(priceTotal, moneyFormat, currency);
    if (wasEl) {
      if (compareTotal > priceTotal) {
        wasEl.textContent = formatMoney(compareTotal, moneyFormat, currency);
        wasEl.hidden = false;
      } else {
        wasEl.hidden = true;
      }
    }

    const addAll = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.section-bundle-hotspots__add-all')
    );
    if (addAll) addAll.disabled = !allAvailable;

    const unavailableNote = /** @type {HTMLElement | null} */ (
      this.querySelector('[id$="-unavailable"]')
    );
    if (unavailableNote) unavailableNote.hidden = allAvailable;
  }

  wireHoverLinking() {
    // Query after hoistCards so the card list is populated.
    this.cards = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('.bundle-card[data-block-id]'))
    );
    this.hotspots = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('.hotspot[data-id]'))
    );

    /**
     * @param {HTMLElement | undefined} el
     * @param {boolean} on
     */
    const setLinked = (el, on) => {
      if (!el) return;
      if (on) el.setAttribute('data-hover-linked', 'true');
      else el.removeAttribute('data-hover-linked');
    };

    /** @type {Map<string, HTMLElement>} */
    const cardByBlockId = new Map();
    this.cards.forEach((el) => {
      const id = el.dataset.blockId;
      if (id) cardByBlockId.set(id, el);
    });

    /** @type {Map<string, HTMLElement>} */
    const hotspotByBlockId = new Map();
    this.hotspots.forEach((el) => {
      const id = el.dataset.id;
      if (id) hotspotByBlockId.set(id, el);
    });

    /** @param {Event} event */
    const onCardEnter = (event) => {
      const target = /** @type {HTMLElement} */ (event.currentTarget);
      const id = target.dataset.blockId;
      if (id) setLinked(hotspotByBlockId.get(id), true);
    };
    /** @param {Event} event */
    const onCardLeave = (event) => {
      const target = /** @type {HTMLElement} */ (event.currentTarget);
      const id = target.dataset.blockId;
      if (id) setLinked(hotspotByBlockId.get(id), false);
    };
    /** @param {Event} event */
    const onHotspotEnter = (event) => {
      const target = /** @type {HTMLElement} */ (event.currentTarget);
      const id = target.dataset.id;
      if (id) setLinked(cardByBlockId.get(id), true);
    };
    /** @param {Event} event */
    const onHotspotLeave = (event) => {
      const target = /** @type {HTMLElement} */ (event.currentTarget);
      const id = target.dataset.id;
      if (id) setLinked(cardByBlockId.get(id), false);
    };

    this.cards.forEach((card) => {
      card.addEventListener('mouseenter', onCardEnter);
      card.addEventListener('mouseleave', onCardLeave);
      card.addEventListener('focusin', onCardEnter);
      card.addEventListener('focusout', onCardLeave);
    });
    this.hotspots.forEach((hotspot) => {
      hotspot.addEventListener('mouseenter', onHotspotEnter);
      hotspot.addEventListener('mouseleave', onHotspotLeave);
      hotspot.addEventListener('focusin', onHotspotEnter);
      hotspot.addEventListener('focusout', onHotspotLeave);
    });

    this.cleanupHoverLinking = () => {
      this.cards.forEach((card) => {
        card.removeEventListener('mouseenter', onCardEnter);
        card.removeEventListener('mouseleave', onCardLeave);
        card.removeEventListener('focusin', onCardEnter);
        card.removeEventListener('focusout', onCardLeave);
      });
      this.hotspots.forEach((hotspot) => {
        hotspot.removeEventListener('mouseenter', onHotspotEnter);
        hotspot.removeEventListener('mouseleave', onHotspotLeave);
        hotspot.removeEventListener('focusin', onHotspotEnter);
        hotspot.removeEventListener('focusout', onHotspotLeave);
      });
    };
  }

  wireBulkAdd() {
    this.addAllButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.section-bundle-hotspots__add-all')
    );
    if (!this.addAllButton) return;

    /** @param {Event} event */
    this.handleAddAll = async (event) => {
      event.preventDefault();
      const button = this.addAllButton;
      if (!button || button.disabled) return;

      const variantIds = this.collectVariantIds();
      if (variantIds.length === 0) return;

      this.setButtonLoading(true);

      try {
        const cartItemSectionIds = /** @type {HTMLElement[]} */ (
          Array.from(document.querySelectorAll('cart-items-component'))
        )
          .map((el) => el.dataset.sectionId)
          .filter(/** @returns {id is string} */ (id) => Boolean(id));

        /** @type {{items: Array<{id: number, quantity: number}>, sections?: string}} */
        const payload = {
          items: variantIds.map((id) => ({ id, quantity: 1 })),
        };
        if (cartItemSectionIds.length) {
          payload.sections = cartItemSectionIds.join(',');
        }

        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);

        const result = await response.json();
        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
        const cart = cartResponse.ok ? await cartResponse.json() : undefined;

        this.dispatchEvent(
          new CartAddEvent(cart, this.id || 'bundle-hotspots', {
            source: 'bundle-hotspots',
            itemCount: variantIds.length,
            sections: result.sections,
          })
        );
      } catch {
        this.flashError();
      } finally {
        this.setButtonLoading(false);
      }
    };

    this.addAllButton.addEventListener('click', this.handleAddAll);
    this.cleanupBulkAdd = () => {
      if (this.addAllButton && this.handleAddAll) {
        this.addAllButton.removeEventListener('click', this.handleAddAll);
      }
    };
  }

  /** @returns {number[]} */
  collectVariantIds() {
    const cards = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.bundle-card[data-product-variant-id]')
    );
    return Array.from(cards)
      .map((card) => parseInt(card.dataset.productVariantId ?? '', 10))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  /** @param {boolean} loading */
  setButtonLoading(loading) {
    if (!this.addAllButton) return;
    this.addAllButton.disabled = loading;
    this.addAllButton.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  flashError() {
    const button = this.addAllButton;
    if (!button) return;
    button.setAttribute('data-error', 'true');
    setTimeout(() => button.removeAttribute('data-error'), 2000);
  }
}

if (!customElements.get('bundle-hotspots')) {
  customElements.define('bundle-hotspots', BundleHotspots);
}
